import type { HttpClient } from '../http/types';
import type {
  BackendSubtask,
  BackendTask,
  BackendTaskHistoryEvent,
  BackendTaskType,
} from '../types';
import { ENDPOINTS } from './endpoints';

/** Comment em uma task thread. */
export interface TaskComment {
  id: string;
  userId: string;
  content: string;
  createdAt: string;
  editedAt: string | null;
}

/** Thread de discussão de uma task. */
export interface TaskThread {
  id: string;
  projectId: string;
  taskId: string | null;
  workItemId: string | null;
  discussionId: string | null;
  createdAt: string;
  updatedAt: string;
  comments: TaskComment[];
}

export interface MoveTaskBoardPayload {
  readonly projectBoardSectionId: string | null;
  readonly afterId?: string | null;
  readonly beforeId?: string | null;
}

export interface MoveTaskTaskListPayload {
  readonly taskListId: string;
}

export interface OrganizeTaskPayload {
  readonly parentId?: string | null;
  readonly afterId?: string | null;
  readonly beforeId?: string | null;
}

export interface CreateTaskPayload {
  readonly taskListId: string;
  readonly name: string;
  readonly code: string;
  readonly description?: string | null;
  readonly projectBoardSectionId?: string | null;
  readonly ownerUserId?: string | null;
  readonly assignedIds?: string[] | null;
  readonly priority?: string;
  readonly billable?: boolean;
  readonly startDate?: string | null;
  readonly dueDate?: string | null;
  readonly budgetHours?: number | null;
  readonly sequence?: number | null;
  readonly sprintId?: string | null;
  readonly parentId?: string | null;
  readonly boardRank?: string | null;
  readonly hierarchyRank?: string | null;
  readonly typeId?: string | null;
  readonly type?: 'EPIC' | 'FEATURE' | 'USER_STORY' | 'TASK' | 'SUBTASK' | null;
}

export interface UpdateTaskPayload extends Partial<CreateTaskPayload> {
  readonly complete?: boolean;
}

export function createTasksApi(http: HttpClient) {
  return {
    list: (projectId: string, taskListId?: string) => {
      const params = taskListId ? `?taskListId=${taskListId}` : '';
      return http.get<BackendTask[]>(`${ENDPOINTS.tasks.root(projectId)}${params}`);
    },

    getById: (projectId: string, taskId: string) =>
      http.get<BackendTask>(ENDPOINTS.tasks.byId(projectId, taskId)),

    history: (projectId: string, taskId: string) =>
      http.get<BackendTaskHistoryEvent[]>(ENDPOINTS.tasks.history(projectId, taskId)),

    create: (projectId: string, payload: CreateTaskPayload) =>
      http.post<BackendTask>(ENDPOINTS.tasks.root(projectId), payload),

    update: (projectId: string, taskId: string, payload: UpdateTaskPayload) =>
      http.patch<void>(ENDPOINTS.tasks.byId(projectId, taskId), payload),

    delete: (projectId: string, taskId: string) =>
      http.delete<void>(ENDPOINTS.tasks.byId(projectId, taskId)),

    /** Organiza a tarefa na hierarquia do projeto (lista + ordem). */
    organization: (projectId: string, taskId: string, payload: OrganizeTaskPayload) =>
      http.patch<void>(ENDPOINTS.tasks.organization(projectId, taskId), payload),

    /** Move o card no quadro (coluna + ordem dentro dela). */
    board: (projectId: string, taskId: string, payload: MoveTaskBoardPayload) =>
      http.patch<void>(ENDPOINTS.tasks.moveOnBoard(projectId, taskId), payload),

    /** Move a tarefa para uma lista nomeada de tarefas. */
    taskList: (projectId: string, taskId: string, taskListId: string) =>
      http.patch<void>(ENDPOINTS.tasks.moveOnTaskList(projectId, taskId), { taskListId }),

    listComments: (projectId: string, taskId: string) =>
      http.get<TaskComment[]>(ENDPOINTS.tasks.comments(projectId, taskId)),

    createComment: (projectId: string, taskId: string, content: string, replyId?: string) =>
      http.post<TaskComment>(ENDPOINTS.tasks.comments(projectId, taskId), { content, replyId }),

    listSubtaskComments: (projectId: string, taskId: string, subtaskId: string) =>
      http.get<TaskComment[]>(ENDPOINTS.tasks.subtaskComments(projectId, taskId, subtaskId)),

    createSubtaskComment: (projectId: string, taskId: string, subtaskId: string, content: string) =>
      http.post<TaskComment>(ENDPOINTS.tasks.subtaskComments(projectId, taskId, subtaskId), {
        content,
      }),

    updateComment: (projectId: string, taskId: string, commentId: string, content: string) =>
      http.patch<TaskComment>(ENDPOINTS.tasks.commentById(projectId, taskId, commentId), {
        content,
      }),

    deleteComment: (projectId: string, taskId: string, commentId: string) =>
      http.delete<void>(ENDPOINTS.tasks.commentById(projectId, taskId, commentId)),

    listSubtasks: (projectId: string, taskId: string) =>
      http.get<BackendSubtask[]>(ENDPOINTS.tasks.subtasks(projectId, taskId)),

    createSubtask: (projectId: string, taskId: string, name: string, userId?: string) =>
      http.post<BackendSubtask>(ENDPOINTS.tasks.subtasks(projectId, taskId), { name, userId }),

    updateSubtask: (
      projectId: string,
      taskId: string,
      subtaskId: string,
      payload: { name?: string; userId?: string | null; sequence?: number },
    ) =>
      http.patch<BackendSubtask>(
        ENDPOINTS.tasks.subtaskById(projectId, taskId, subtaskId),
        payload,
      ),

    addSubtaskUser: (projectId: string, taskId: string, subtaskId: string, userId: string) =>
      http.post<void>(ENDPOINTS.tasks.subtaskUsers(projectId, taskId, subtaskId), { userId }),

    changeSubtaskCompletion: (
      projectId: string,
      taskId: string,
      subtaskId: string,
      complete: boolean,
    ) =>
      http.patch<BackendSubtask>(ENDPOINTS.tasks.subtaskCompletion(projectId, taskId, subtaskId), {
        complete,
      }),

    deleteSubtask: (projectId: string, taskId: string, subtaskId: string) =>
      http.delete<void>(ENDPOINTS.tasks.subtaskById(projectId, taskId, subtaskId)),
  };
}

export function createTaskTypesApi(http: HttpClient) {
  return {
    list: (projectId: string) => http.get<BackendTaskType[]>(ENDPOINTS.taskTypes.root(projectId)),
  };
}

export type TasksApi = ReturnType<typeof createTasksApi>;
export type TaskTypesApi = ReturnType<typeof createTaskTypesApi>;
